// Minimal ZIP writer using the STORE method (no compression). Packet PDFs are
// already deflate-compressed internally, so stored entries cost almost nothing
// and this avoids taking a jszip dependency for the Open Trade Packet zips.
// Produces a standard archive readable by Windows Explorer, macOS Archive
// Utility, and mail clients.

export type ZipEntry = {
  name: string
  data: Uint8Array
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    }
    table[n] = c >>> 0
  }
  return table
})()

export const crc32 = (data: Uint8Array): number => {
  let crc = 0xffffffff
  for (let i = 0; i < data.length; i++) {
    crc = CRC_TABLE[(crc ^ data[i]) & 0xff] ^ (crc >>> 8)
  }
  return (crc ^ 0xffffffff) >>> 0
}

// MS-DOS date/time as stored in zip headers (2-second resolution, local time).
const toDosDateTime = (d: Date): { date: number; time: number } => ({
  date:
    ((Math.max(0, d.getFullYear() - 1980) & 0x7f) << 9) |
    ((d.getMonth() + 1) << 5) |
    d.getDate(),
  time: (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1),
})

const UTF8_FLAG = 0x0800

// Windows Explorer's built-in extractor enforces a 260-character limit on the
// full extracted path (destination folder + folders inside the zip + filename).
// The Open Trade Packet reserves 160 of those for wherever the agent bank
// extracts, leaving this much for "<zipName>/<fileName>". Mirrors
// PACKET_PATH_MAX in the backend's open-trade-packet-filenames.ts.
export const OPEN_TRADE_PACKET_PATH_MAX = 100

// Longest entry filename the packet may carry, with the extension. Mirrors
// PDF_FILE_NAME_MAX in the backend's open-trade-packet-filenames.ts.
const ENTRY_NAME_MAX = 57

// Duplicate names make an archive whose entries overwrite each other on
// extraction; suffix repeats with "_n" before the extension. The suffix is
// space-free and the base shrinks to absorb it, so deduping can neither
// introduce a space nor push an entry past ENTRY_NAME_MAX. Uniqueness is
// checked against every name emitted so far, not just the originals, so
// "a.pdf, a.pdf, a_2.pdf" cannot collide either. Backend names are already
// deduped — this is the last line of defence before the bytes are written.
export const dedupeEntryNames = (entries: ZipEntry[]): ZipEntry[] => {
  const used = new Set<string>()
  return entries.map(entry => {
    if (!used.has(entry.name)) {
      used.add(entry.name)
      return entry
    }
    const dot = entry.name.lastIndexOf('.')
    const base = dot > 0 ? entry.name.slice(0, dot) : entry.name
    const ext = dot > 0 ? entry.name.slice(dot) : ''
    let n = 2
    let name: string
    do {
      const suffix = `_${n++}`
      const room = ENTRY_NAME_MAX - suffix.length - ext.length
      name = `${base.length > room ? base.slice(0, room) : base}${suffix}${ext}`
    } while (used.has(name))
    used.add(name)
    return { ...entry, name }
  })
}

export const buildZip = (entries: ZipEntry[], now: Date = new Date()): Uint8Array => {
  const encoder = new TextEncoder()
  const { date, time } = toDosDateTime(now)

  const localParts: Uint8Array[] = []
  const centralParts: Uint8Array[] = []
  let offset = 0

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.name)
    const crc = crc32(entry.data)
    const size = entry.data.length

    const local = new Uint8Array(30 + nameBytes.length)
    const lv = new DataView(local.buffer)
    lv.setUint32(0, 0x04034b50, true) // local file header signature
    lv.setUint16(4, 20, true) // version needed to extract
    lv.setUint16(6, UTF8_FLAG, true) // general purpose bit flag
    lv.setUint16(8, 0, true) // compression method: STORE
    lv.setUint16(10, time, true)
    lv.setUint16(12, date, true)
    lv.setUint32(14, crc, true)
    lv.setUint32(18, size, true) // compressed size (== uncompressed for STORE)
    lv.setUint32(22, size, true) // uncompressed size
    lv.setUint16(26, nameBytes.length, true)
    lv.setUint16(28, 0, true) // extra field length
    local.set(nameBytes, 30)

    const central = new Uint8Array(46 + nameBytes.length)
    const cv = new DataView(central.buffer)
    cv.setUint32(0, 0x02014b50, true) // central directory header signature
    cv.setUint16(4, 20, true) // version made by
    cv.setUint16(6, 20, true) // version needed to extract
    cv.setUint16(8, UTF8_FLAG, true)
    cv.setUint16(10, 0, true) // compression method: STORE
    cv.setUint16(12, time, true)
    cv.setUint16(14, date, true)
    cv.setUint32(16, crc, true)
    cv.setUint32(20, size, true)
    cv.setUint32(24, size, true)
    cv.setUint16(28, nameBytes.length, true)
    cv.setUint16(30, 0, true) // extra field length
    cv.setUint16(32, 0, true) // file comment length
    cv.setUint16(34, 0, true) // disk number start
    cv.setUint16(36, 0, true) // internal file attributes
    cv.setUint32(38, 0, true) // external file attributes
    cv.setUint32(42, offset, true) // relative offset of local header
    central.set(nameBytes, 46)

    localParts.push(local, entry.data)
    centralParts.push(central)
    offset += local.length + entry.data.length
  }

  const centralSize = centralParts.reduce((acc, p) => acc + p.length, 0)
  const eocd = new Uint8Array(22)
  const ev = new DataView(eocd.buffer)
  ev.setUint32(0, 0x06054b50, true) // end of central directory signature
  ev.setUint16(4, 0, true) // number of this disk
  ev.setUint16(6, 0, true) // disk with the start of the central directory
  ev.setUint16(8, entries.length, true)
  ev.setUint16(10, entries.length, true)
  ev.setUint32(12, centralSize, true)
  ev.setUint32(16, offset, true) // offset of start of central directory
  ev.setUint16(20, 0, true) // comment length

  const total =
    localParts.reduce((acc, p) => acc + p.length, 0) + centralSize + eocd.length
  const out = new Uint8Array(total)
  let pos = 0
  for (const part of [...localParts, ...centralParts, eocd]) {
    out.set(part, pos)
    pos += part.length
  }
  return out
}
